<template>
  <div class="banned-sending-ips">
    <div
      class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom"
    >
      <h1 class="h2">Banned sending IPs</h1>
    </div>

    <p class="text-muted">
      Banned sending IPs/ranges will not pass aws-smtp for sending. Use a single
      address or a CIDR range (a single IP is stored as /32 or /128).
    </p>

    <form @submit.prevent="addBanned" class="mb-3">
      <div class="form-row align-items-end">
        <div class="col-4">
          <label class="mb-1">IP / range</label>
          <input
            type="text"
            placeholder="1.2.3.0/24 or 2001:db8::/32"
            class="form-control"
            v-model="banIpRange"
          />
        </div>
        <div class="col">
          <label class="mb-1">Reason</label>
          <input
            type="text"
            placeholder="Reason"
            class="form-control"
            v-model="banReason"
          />
        </div>
        <div class="col">
          <label class="mb-1"
            >Expires at (UTC, optional, YYYY-MM-DD HH:MM:SS)</label
          >
          <input
            type="text"
            placeholder="YYYY-MM-DD HH:MM:SS"
            class="form-control"
            v-model="banExpiresAt"
          />
        </div>
        <div class="col-auto">
          <input
            type="submit"
            name="ban"
            value="Ban IP"
            class="btn btn-sm btn-warning"
          />
        </div>
      </div>
    </form>

    <div class="form-row mb-3">
      <div class="col-4">
        <input
          type="text"
          class="form-control"
          placeholder="Search by IP / range…"
          v-model="banSearch"
        />
      </div>
    </div>

    <table v-if="filteredBanned.length" class="table table-striped table-hover">
      <thead>
        <tr>
          <th>IP / range</th>
          <th>Reason</th>
          <th>Banned by</th>
          <th>Created at (UTC)</th>
          <th>Expires at (UTC)</th>
          <th class="text-right">Unban</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in paginatedBanned" v-bind:key="item.ip_range">
          <td>{{ item.ip_range }}</td>
          <td>{{ item.reason }}</td>
          <td>{{ item.banned_by }}</td>
          <td>{{ item.created_at }}</td>
          <td>{{ item.expires_at || "Never" }}</td>
          <td class="text-right">
            <a
              href="javascript:;"
              title="Unban this range"
              class="btn btn-danger"
              v-on:click="removeBanned(item.ip_range)"
              >Unban</a
            >
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="alert alert-primary" role="alert">
      {{
        banSearch ? "No IPs match your search." : "No banned sending IPs yet."
      }}
    </div>

    <div
      v-if="banPageCount > 1"
      class="d-flex justify-content-between align-items-center mt-4 pt-3 mb-5 border-top"
    >
      <span class="text-muted">
        Showing {{ (banPage - 1) * pageSize + 1 }}–{{
          Math.min(banPage * pageSize, filteredBanned.length)
        }}
        of {{ filteredBanned.length }}
      </span>
      <div>
        <button
          class="btn btn-sm btn-primary mr-2"
          :disabled="banPage === 1"
          @click="banPage--"
        >
          Previous
        </button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="banPage >= banPageCount"
          @click="banPage++"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatHttpError } from "../utils/errors";

export default {
  data() {
    return {
      banned: [],
      banIpRange: null,
      banReason: null,
      banExpiresAt: null,
      banSearch: "",
      banPage: 1,
      pageSize: 40,
    };
  },
  created() {
    this.loadBanned();
    this.autoBanFromQuery();
  },
  computed: {
    // Bans with an expiry float to the top, soonest-expiring first; permanent bans (no expires_at) follow.
    filteredBanned() {
      const query = this.banSearch.trim().toLowerCase();
      const matches = query
        ? this.banned.filter((item) =>
            item.ip_range.toLowerCase().includes(query),
          )
        : this.banned;
      return [...matches].sort((a, b) => {
        if (a.expires_at && b.expires_at) {
          return new Date(a.expires_at) - new Date(b.expires_at);
        }
        if (a.expires_at) return -1;
        if (b.expires_at) return 1;
        return 0;
      });
    },
    paginatedBanned() {
      const start = (this.banPage - 1) * this.pageSize;
      return this.filteredBanned.slice(start, start + this.pageSize);
    },
    banPageCount() {
      return Math.ceil(this.filteredBanned.length / this.pageSize);
    },
  },
  watch: {
    banSearch() {
      this.banPage = 1;
    },
  },
  methods: {
    // Lets Slack abuse alerts link straight to a one-click ban, e.g.
    // /banned-sending-ips?ban=true&ip_range=1.2.3.0/24&reason=Snowshoe%20spam
    autoBanFromQuery() {
      const { ban, ip_range, reason } = this.$route.query;
      if (ban !== "true" || !ip_range) return;
      this.banIpRange = ip_range;
      this.banReason = reason || "";
      this.addBanned(() => {
        alert(`IP: ${ip_range} banned for reason: ${reason}`);
      });
    },
    loadBanned() {
      this.$http.get("banned-sending-ips").then(
        (response) => {
          this.banned = response.body.items;
        },
        (response) => {
          if (response.status === 401) return;
          alert(formatHttpError(response));
        },
      );
    },
    addBanned(onSuccess) {
      const ipRange = (this.banIpRange || "").trim();
      const reason = (this.banReason || "").trim();
      if (!ipRange || !reason) {
        return alert("IP / range and reason are required.");
      }
      const payload = { ip_range: ipRange, reason };
      const expiresAt = (this.banExpiresAt || "").trim();
      if (expiresAt) payload.expires_at = expiresAt;
      this.$http.post("banned-sending-ips", payload).then(
        (response) => {
          this.banned.unshift(response.body);
          this.banIpRange = null;
          this.banReason = null;
          this.banExpiresAt = null;
          if (onSuccess) onSuccess();
        },
        (response) => {
          if (response.status === 401) return;
          alert(formatHttpError(response));
        },
      );
    },
    removeBanned(ipRange) {
      if (!confirm(`Unban ${ipRange}?`)) return;
      this.$http.delete(`banned-sending-ips/${ipRange}`).then(
        () => {
          const idx = this.banned.findIndex((x) => x.ip_range === ipRange);
          this.banned.splice(idx, 1);
        },
        (response) => {
          alert(formatHttpError(response));
        },
      );
    },
  },
};
</script>
